import { jsxs, jsx } from "react/jsx-runtime";
import { Box, ProgressStepper } from "@zillow/constellation-10";
import React, { useCallback } from "react";
import { getTrackingParamsFromCurrentUrl } from "../../utils/utm.esm.js";
import OverlappingStack from "./OverlappingStack.esm.js";
import { getOrgError, getPhoneError, getEmailError, getNameError } from "./utils.esm.js";
import { useBlockEnvironment } from "../../contexts/BlockEnvironment/BlockEnvironmentProvider.esm.js";
const INVISIBLE_SX = {
  visibility: "hidden",
  pointerEvents: "none",
  userSelect: "none"
};
function FormStepper(props) {
  const steps = React.Children.toArray(props.children).filter((child) => typeof child === "object" && child !== null && "type" in child && child.type === FormStepper.Step);
  const [footer] = React.Children.toArray(props.children).filter((child) => typeof child === "object" && child !== null && "type" in child && child.type === FormStepper.Footer);
  const visibleStep = steps.find((step) => step.props.name === props.visibleStepName);
  const hasProgressBar = !!(visibleStep == null ? void 0 : visibleStep.props.progressBar);
  const {
    environment = "prod"
  } = useBlockEnvironment();
  return /* @__PURE__ */ jsxs(Box, { children: [
    hasProgressBar && /* @__PURE__ */ jsx(
      ProgressStepper,
      {
        "aria-label": "progress",
        css: {
          margin: "0 auto",
          marginBottom: "looser",
          maxWidth: "540px"
        },
        activeStepIndex: visibleStep.props.progressBar.current - 1,
        steps: Array.from({
          length: visibleStep.props.progressBar.total
        }, (_, i) => ({
          label: "",
          tone: i < visibleStep.props.progressBar.current ? "complete" : void 0
        }))
      }
    ),
    /* @__PURE__ */ jsx(OverlappingStack, { css: {
      // v8 $spacing.xxl → layout.vast.default (64px) per v8-to-v10.js
      marginBottom: "layout.vast.default",
      maxWidth: "540px",
      margin: "0 auto"
    }, asChild: true, children: /* @__PURE__ */ jsxs(
      "form",
      {
        ref: useCallback(function prefileUtmParams(form) {
          if (!form) {
            return;
          }
          const currentTrackingValues = getTrackingParamsFromCurrentUrl();
          for (const [key, value] of Object.entries(currentTrackingValues)) {
            const element = form.elements[key];
            if (element) {
              element.value = value;
            }
          }
        }, []),
        onSubmit: async (e) => {
          var _a, _b, _c;
          e.preventDefault();
          if (!props.onShouldSubmit()) {
            return;
          }
          (_a = props.onSubmitStart) == null ? void 0 : _a.call(props);
          const formData = new FormData(e.currentTarget);
          const rawData = {};
          for (const [key, value] of formData.entries()) {
            rawData[key] = typeof value === "string" ? value : "";
          }
          const data = ((_b = props.onPrepareSubmitData) == null ? void 0 : _b.call(props, rawData)) || rawData;
          const validationErrors = {
            Name: getNameError(data.Name),
            Email: getEmailError(data.Email),
            Phone: getPhoneError(data.Phone),
            Brokerage_Size: getOrgError(data.Brokerage_Size)
          };
          const hasValidationErrors = Object.values(validationErrors).some((error) => Boolean(error));
          if (hasValidationErrors) {
            (_c = props.onSubmitError) == null ? void 0 : _c.call(props);
            return;
          }
          if (data.URL) {
            try {
              const url = new URL(data.URL);
              data.URL = `${url.origin}${url.pathname}`;
            } catch {
              delete data.URL;
            }
          }
          if (data.URL_Referrer) {
            try {
              const url = new URL(data.URL_Referrer);
              data.URL_Referrer = `${url.origin}${url.pathname}`;
            } catch {
              delete data.URL_Referrer;
            }
          }
          if (typeof data.Name === "string") {
            const [firstName, ...lastNameParts] = data.Name.split(" ");
            data["First_Name"] = firstName;
            data["Last_Name"] = lastNameParts.join(" ");
          }
          data["tfa_dbFormId"] = "WORDPRESS";
          data["Ok_to_contact"] = "1";
          try {
            const res = await fetch(environment === "prod" ? "https://zg-api.com/interested-party/submit" : "https://zg-api.test.zillow.net/interested-party/submit", {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded"
              },
              body: new URLSearchParams(data).toString()
            });
            if (res.status !== 200) {
              throw new Error(`Got failed status code: "${res.status}"`);
            }
            props.onSubmitSuccess();
          } catch (error) {
            props.onSubmitError();
          }
        },
        noValidate: true,
        children: [
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "utm_medium" }),
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "utm_term" }),
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "utm_content" }),
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "utm_source" }),
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "utm_campaign" }),
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "Internal_Tracking_Code" }),
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "URL" }),
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "URL_Referrer" }),
          /* @__PURE__ */ jsx("input", { type: "hidden", name: "Test_Variation" }),
          steps.map((step) => /* @__PURE__ */ jsx(Box, { style: step.props.name === props.visibleStepName ? void 0 : INVISIBLE_SX, children: step }, step.props.name))
        ]
      }
    ) }),
    footer && /* @__PURE__ */ jsx(Box, { css: {
      // v8 $breakpoints.lg = 768px
      maxWidth: "768px",
      margin: "0 auto"
    }, children: footer })
  ] });
}
FormStepper.Step = (props) => props.children;
FormStepper.Footer = (props) => props.children;
export {
  INVISIBLE_SX,
  FormStepper as default
};
